import { ROLE_KINDS, type RoleKind, type RoleUse, roleKindAllows } from '@felix/domain';
import {
  ActionIcon,
  Button,
  Menu,
  Modal,
  Select,
  Switch,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconDots, IconLock, IconPlus, IconSearch } from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { sortItems } from '../purchasing/requisitions.js';
import {
  LoadError,
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../purchasing/table.js';
import classes from './Setup.module.css';
import { type SetupSource, setupErrorCode } from './setup-source.js';
import type { RoleItem, RoleList } from './setup-types.js';

const PAGE_SIZE = 10;

type Column = 'order' | 'name' | 'description' | 'pms' | 'kind' | 'users' | 'status';

/**
 * Rolün görünen adı: varsayılan rol ve adı değiştirilmemiş ofis ünvanı i18n'den (ADR 0042 §23;
 * ADR 0143 §5), şirketin rolü kendi adıyla.
 */
export function roleLabel(
  t: TFunction,
  role: { name: string; defaultKey: string | null; titleKey?: string | null },
): string {
  const key = role.defaultKey ?? role.titleKey ?? null;
  return key ? t(`setup.roles.names.${key}`, { defaultValue: role.name }) : role.name;
}

/** Rolün türü; eski sunucu göndermez (`any`). */
export const roleKindOf = (role: { kind?: RoleKind | undefined }): RoleKind => role.kind ?? 'any';

/**
 * Seçim listesinin rolleri (ADR 0143 §4): şirket rolü `office` + `any`, rütbe `vessel` + `any`.
 * Satırda seçili rol türü uymasa da kalır: migration'dan önce yazılmış atama bozulmaz.
 */
export function rolesFor<T extends { id: string; kind?: RoleKind | undefined }>(
  roles: readonly T[],
  use: RoleUse,
  selectedId?: string | null,
): T[] {
  return roles.filter((role) => role.id === selectedId || roleKindAllows(roleKindOf(role), use));
}

/**
 * Hata metni: bilinen kod çevrilir, bilinmeyen genel metne düşer. Giriş yapabilen yönetici
 * kalmazdıysa (`noSignIn`; ADR 0150 §3 Ek) metin ne yapılacağını söyler: önce yeni parola.
 */
export function setupErrorText(t: TFunction, code: string, detail?: string): string {
  if (code === 'LAST_ADMIN' && detail === 'self') return t('setup.errors.LAST_ADMIN_SELF');
  if ((code === 'LAST_ADMIN' || code === 'MATRIX_LOCKOUT') && detail === 'noSignIn') {
    return t(`setup.errors.${code}_NO_SIGN_IN`);
  }
  return t(`setup.errors.${code}`, { defaultValue: t('setup.errors.GENERIC') });
}

export interface RolesTableProps {
  roles: RoleItem[];
  canEdit: boolean;
  canRetire: boolean;
  onEdit: (role: RoleItem) => void;
  onRetire: (role: RoleItem) => void;
}

/** Roller tablosu: varsayılan roller önce (tanım sırası), sonra şirketin rolleri. */
export function RolesTable({ roles, canEdit, canRetire, onEdit, onRetire }: RolesTableProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.roles.${key}`, options);
  const [sort, setSort] = useState<SortState<Column>>({ key: 'order', dir: 'asc' });
  const [page, setPage] = useState(1);
  const order = new Map(roles.map((role, index) => [role.id, index]));
  const value: Record<Column, (role: RoleItem) => string | number> = {
    order: (role) => order.get(role.id) ?? 0,
    name: (role) => roleLabel(t, role),
    description: (role) =>
      role.description ?? (role.defaultKey ? k(`descriptions.${role.defaultKey}`) : ''),
    pms: (role) => (role.pmsResponsible ? 0 : 1),
    kind: (role) => ROLE_KINDS.indexOf(roleKindOf(role)),
    users: (role) => role.users,
    status: (role) => (role.inUse ? 0 : 1),
  };
  // Varsayılan rolün açıklaması yoksa i18n'deki tanımı soluk gösterilir
  const description = (role: RoleItem) =>
    role.description ?? (role.defaultKey ? k(`descriptions.${role.defaultKey}`) : undefined);
  const sorted = sortItems(roles, sort.dir, value[sort.key]);
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = sorted.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  return (
    <>
      <section className={table.tableWrap} aria-label={k('table')}>
        <table className={`${table.table} ${classes.tableWide}`}>
          <colgroup>
            <col className={classes.w22} />
            <col className={classes.w24} />
            <col className={classes.w14} />
            <col className={classes.w14} />
            <col className={classes.w8} />
            <col className={classes.w10} />
            <col className={classes.w8} />
          </colgroup>
          <thead>
            <tr>
              {(['name', 'description', 'pms', 'kind', 'users', 'status'] as const).map(
                (column) => (
                  <SortableTh
                    key={column}
                    column={column}
                    label={k(`columns.${column}`)}
                    sort={sort}
                    onSort={setSort}
                    {...(column === 'name' ? { className: classes.key } : {})}
                    {...(column === 'users' ? { numeric: true } : {})}
                  />
                ),
              )}
              <th scope="col" data-numeric>
                {k('columns.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((role) => (
              <tr key={role.id}>
                <td className={`${table.itemName} ${classes.key}`} title={roleLabel(t, role)}>
                  <span className={classes.roleName}>
                    <span>{roleLabel(t, role)}</span>
                    {role.defaultKey ? (
                      <StatusBadge tone="info" icon={<IconLock size={12} />}>
                        {k('default')}
                      </StatusBadge>
                    ) : null}
                  </span>
                </td>
                <td title={description(role)}>
                  {role.description ?? (
                    <span className={table.secondary}>{description(role) ?? '—'}</span>
                  )}
                </td>
                <td>{role.pmsResponsible ? k('yes') : k('no')}</td>
                <td>{k(`kinds.${roleKindOf(role)}`)}</td>
                <td data-numeric>{role.users}</td>
                <td>
                  <StatusBadge tone={role.inUse ? 'success' : 'neutral'}>
                    {role.inUse ? k('inUse') : k('unused')}
                  </StatusBadge>
                </td>
                <td data-numeric>
                  {canEdit || canRetire ? (
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={k('actionsOf', { name: roleLabel(t, role) })}
                        >
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {canEdit ? (
                          <Menu.Item onClick={() => onEdit(role)}>{k('edit')}</Menu.Item>
                        ) : null}
                        {canRetire ? (
                          <Menu.Item
                            disabled={role.defaultKey !== null || role.inUse}
                            onClick={() => onRetire(role)}
                          >
                            {k('retire')}
                          </Menu.Item>
                        ) : null}
                      </Menu.Dropdown>
                    </Menu>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <TablePager
        page={current}
        pages={pages}
        summary={k('showing', {
          from: (current - 1) * PAGE_SIZE + 1,
          to: Math.min(current * PAGE_SIZE, sorted.length),
          total: sorted.length,
        })}
        label={k('pagination')}
        onPage={setPage}
      />
    </>
  );
}

/** Düzenleme penceresinde seçilebilen türler (`roleKindChoices`). */
export interface RoleKindChoices {
  kinds: RoleKind[];
  /** Seçim kapalı: varsayılan rol sistem türünde (ya da sunucu sistem türünü göndermiyor). */
  locked: boolean;
  /** Varsayılan rolün türü kilitten önce kaymış: yalnız bu sistem türüne dönülür. */
  restore: RoleKind | null;
  /**
   * Yönetici rolü, işlemi yapan yönetici değil: tür değiştirilebilirdi ama yalnız yöneticiden
   * (ADR 0150 §2). Yalnız bu durumda verilir.
   */
  adminOnly?: true;
}

/**
 * Rol türü seçenekleri (ADR 0143 §4; ADR 0150 §2): yeni ve şirketin rolünde bütün türler.
 * Varsayılan rolde tür kilitlidir; türü kilitten önce sistem türünden ayrılmışsa seçim yalnız
 * {sistem türü, mevcut tür} ile açık kalır (yanlış tür kalıcı olmaz). Yönetici rolünün (ADR 0150
 * §1) türünü yalnız yönetici değiştirir: yönetici olmayana seçim mevcut türde kapalıdır, nedeni
 * ayrı (ADR 0151 Ek; sunucu `FORBIDDEN` verir). İşlemi yapanı göndermeyen eski sunucuda kısıt yok.
 */
export function roleKindChoices(
  role:
    | (Pick<RoleItem, 'defaultKey' | 'kind' | 'systemKind'> & Partial<Pick<RoleItem, 'id'>>)
    | null,
  actor: Pick<RoleList, 'actorIsAdmin' | 'adminRoleIds'> = {},
): RoleKindChoices {
  const choices = baseKindChoices(role);
  const adminRole = role?.id !== undefined && (actor.adminRoleIds ?? []).includes(role.id);
  if (role !== null && !choices.locked && actor.actorIsAdmin === false && adminRole) {
    return { kinds: [roleKindOf(role)], locked: true, restore: null, adminOnly: true };
  }
  return choices;
}

function baseKindChoices(
  role: Pick<RoleItem, 'defaultKey' | 'kind' | 'systemKind'> | null,
): RoleKindChoices {
  if (role?.defaultKey == null) return { kinds: [...ROLE_KINDS], locked: false, restore: null };
  const current = roleKindOf(role);
  const system = role.systemKind ?? null;
  if (system === null || system === current) {
    return { kinds: [current], locked: true, restore: null };
  }
  return { kinds: [system, current], locked: false, restore: system };
}

/**
 * Rol türü seçimi (ADR 0143 §4). Varsayılan sekiz rolde adı gibi türü de kilitlidir; seçim kapalı
 * ve nedeni yazılı (ADR 0150 §2). Kaymış varsayılan rolde yalnız sistem türüne dönülür
 * (`restore`); açıklama bunu söyler. Yönetici rolünde yönetici olmayana kapalı (`adminOnly`).
 */
export function RoleKindSelect({
  value,
  locked,
  onChange,
  kinds = ROLE_KINDS,
  restore = null,
  adminOnly = false,
}: {
  value: RoleKind;
  locked: boolean;
  onChange: (kind: RoleKind) => void;
  kinds?: readonly RoleKind[];
  restore?: RoleKind | null;
  adminOnly?: boolean;
}) {
  const { t } = useTranslation();
  const description = adminOnly
    ? t('setup.roles.dialog.kindAdminOnly')
    : restore !== null
      ? t('setup.roles.dialog.kindRestore', { kind: t(`setup.roles.kinds.${restore}`) })
      : t(locked ? 'setup.roles.dialog.kindLocked' : 'setup.roles.dialog.kindHelp');
  return (
    <Select
      label={t('setup.roles.dialog.kind')}
      description={description}
      data={kinds.map((kind) => ({ value: kind, label: t(`setup.roles.kinds.${kind}`) }))}
      value={value}
      allowDeselect={false}
      disabled={locked}
      onChange={(next) => onChange((next as RoleKind | null) ?? 'any')}
      mb="sm"
    />
  );
}

function RoleDialog({
  role,
  actor,
  source,
  onClose,
  onSaved,
}: {
  role: RoleItem | null;
  /** İşlemi yapan yönetici mi ve yönetici rolleri (`GET /api/office/roles`; eski sunucuda yok). */
  actor: Pick<RoleList, 'actorIsAdmin' | 'adminRoleIds'>;
  source: SetupSource;
  onClose: () => void;
  onSaved: (role: RoleItem) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`setup.roles.dialog.${key}`);
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [pms, setPms] = useState(role?.pmsResponsible ?? false);
  const [kind, setKind] = useState<RoleKind>(role ? roleKindOf(role) : 'any');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const locked = role?.defaultKey != null;
  const choices = roleKindChoices(role, actor);
  return (
    <Modal
      opened
      onClose={onClose}
      title={role ? k('editTitle') : k('newTitle')}
      closeButtonProps={{ 'aria-label': k('cancel') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          try {
            const input = {
              name,
              description: description.trim() || null,
              pmsResponsible: pms,
              kind,
            };
            const saved = role
              ? await source.updateRole(role.id, role.rowVersion, input)
              : await source.createRole(input);
            onSaved(saved);
          } catch (caught) {
            setError(setupErrorText(t, setupErrorCode(caught)));
          } finally {
            setBusy(false);
          }
        }}
      >
        <TextInput
          label={k('name')}
          required
          value={name}
          maxLength={80}
          disabled={locked}
          description={locked ? k('nameLocked') : undefined}
          onChange={(event) => setName(event.currentTarget.value)}
          mb="sm"
        />
        <Textarea
          label={k('description')}
          value={description}
          maxLength={500}
          autosize
          minRows={2}
          onChange={(event) => setDescription(event.currentTarget.value)}
          mb="sm"
        />
        <RoleKindSelect
          value={kind}
          locked={choices.locked}
          kinds={choices.kinds}
          restore={choices.restore}
          adminOnly={choices.adminOnly === true}
          onChange={setKind}
        />
        <Switch
          label={k('pms')}
          description={k('pmsHelp')}
          checked={pms}
          onChange={(event) => setPms(event.currentTarget.checked)}
        />
        {error ? (
          <p className={classes.error} role="alert">
            {error}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {k('cancel')}
          </Button>
          <Button type="submit" loading={busy} disabled={name.trim() === ''}>
            {k('save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; list: RoleList };
type Notice = { tone: 'success' | 'danger'; text: string };

/**
 * Roller (`/settings/roles`; ADR 0042 §23) [sorular #248]: arama, sıralanır ve sayfalanır tablo,
 * "New Role", düzenleme ve emeklilik. Varsayılan sekiz rolün adı kilitli; kullanılan rol emekli
 * olmaz. Silme yoktur.
 */
export function RolesScreen({ source }: { source: SetupSource }) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.roles.${key}`, options);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<RoleItem | 'new' | null>(null);
  const [retiring, setRetiring] = useState<RoleItem | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', list: await source.roles() });
    } catch (error) {
      setState({ kind: 'error', code: setupErrorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state.list : null;
  const roles = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    return (ready?.roles ?? []).filter(
      (role) =>
        !q ||
        [roleLabel(t, role), role.name, role.description ?? ''].some((v) =>
          v.toLocaleLowerCase().includes(q),
        ),
    );
  }, [ready, query, t]);

  return (
    <div className={classes.screen}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        crumb={k('crumb')}
        actions={
          ready?.canCreate ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>
              {k('new')}
            </Button>
          ) : undefined
        }
      />
      {notice ? (
        <p
          className={classes.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      <Panel flush>
        <div className={classes.searchRow}>
          <TextInput
            className={classes.search}
            leftSection={<IconSearch size={16} aria-hidden />}
            placeholder={k('search')}
            aria-label={k('search')}
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
          />
        </div>
        {state.kind === 'error' ? (
          <LoadError title={k('loadError')} code={state.code} onRetry={() => void load()} />
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={7} label={k('table')} />
        ) : (ready?.roles.length ?? 0) === 0 ? (
          <EmptyState title={k('emptyTitle')} description={k('emptyText')} />
        ) : roles.length === 0 ? (
          <NoMatch title={k('noMatch')} onClear={() => setQuery('')} />
        ) : (
          <RolesTable
            roles={roles}
            canEdit={ready?.canEdit ?? false}
            canRetire={ready?.canRetire ?? false}
            onEdit={setEditing}
            onRetire={setRetiring}
          />
        )}
      </Panel>
      {editing ? (
        <RoleDialog
          role={editing === 'new' ? null : editing}
          actor={ready ?? {}}
          source={source}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setNotice({ tone: 'success', text: k('saved', { name: roleLabel(t, saved) }) });
            void load();
          }}
        />
      ) : null}
      {retiring ? (
        <Modal
          opened
          onClose={() => setRetiring(null)}
          closeButtonProps={{ 'aria-label': t('setup.close') }}
          title={k('retireDialog.title')}
        >
          <p className={classes.muted}>
            {k('retireDialog.body', { name: roleLabel(t, retiring) })}
          </p>
          <div className={classes.dialogActions}>
            <Button variant="default" onClick={() => setRetiring(null)}>
              {k('dialog.cancel')}
            </Button>
            <Button
              color="red"
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await source.retireRole(retiring.id, retiring.rowVersion);
                  setNotice({
                    tone: 'success',
                    text: k('retired', { name: roleLabel(t, retiring) }),
                  });
                } catch (error) {
                  setNotice({ tone: 'danger', text: setupErrorText(t, setupErrorCode(error)) });
                } finally {
                  setBusy(false);
                  setRetiring(null);
                  void load();
                }
              }}
            >
              {k('retireDialog.confirm')}
            </Button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
